import {useState} from 'react';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {UserPlus, Loader2} from 'lucide-react';
import {CreateRegistrationResult, INVITE_EXPIRATION_DAY_OPTIONS} from '@internal/pages/users/new';
import {FeatureFlagEditor, getDefaultFeatureFlags} from './featureFlagConfig';
import {AssignableRolePicker} from './AssignableRolePicker';

export function InternalUserForm({
  onSuccess,
}: {
  onSuccess: (data: CreateRegistrationResult) => void;
}) {
  const [internalForm, setInternalForm] = useState({
    email: '',
    name: '',
    role: 'internal:admin' as string,
    expiryDays: 7,
    featureFlags: getDefaultFeatureFlags(),
  });

  const createInternalUser = trpc.internal.userManagement.createInternalUser.useMutation({
    onSuccess: (data) => {
      onSuccess(data.registration);
      toast({
        title: 'Registration created',
        description: `Access code has been generated for ${internalForm.email}`,
      });
    },
    onError: createMutationErrorHandler('Error creating registration'),
  });

  const handleInternalSubmit = () => {
    createInternalUser.mutate(internalForm);
  };

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>
          Internal User Registration
        </CardTitle>
      </CardHeader>
      <CardContent className='p-6 space-y-6'>
        <div className='grid grid-cols-2 gap-6'>
          <div className='space-y-2'>
            <Label htmlFor='internal-email'>Email Address</Label>
            <Input
              id='internal-email'
              type='email'
              placeholder='user@example.com'
              value={internalForm.email}
              onChange={(e) => setInternalForm({...internalForm, email: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='internal-name'>Full Name</Label>
            <Input
              id='internal-name'
              placeholder='John Doe'
              value={internalForm.name}
              onChange={(e) => setInternalForm({...internalForm, name: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
        </div>
        <AssignableRolePicker
          scope='INTERNAL'
          idPrefix='internal'
          value={internalForm.role}
          onChange={(role) => setInternalForm({...internalForm, role})}
        />
        <FeatureFlagEditor
          idPrefix='internal'
          flags={internalForm.featureFlags}
          onChange={(featureFlags) => setInternalForm({...internalForm, featureFlags})}
        />
        <div className='space-y-2'>
          <Label htmlFor='internal-expiry'>Code Expiry (days)</Label>
          <Select
            value={internalForm.expiryDays.toString()}
            onValueChange={(value) =>
              setInternalForm({...internalForm, expiryDays: parseInt(value)})
            }
          >
            <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITE_EXPIRATION_DAY_OPTIONS.map((days) => (
                <SelectItem key={days} value={days.toString()}>
                  {days} day{days > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
          onClick={handleInternalSubmit}
          disabled={
            !internalForm.email ||
            !internalForm.name ||
            !internalForm.role ||
            createInternalUser.isPending
          }
        >
          {createInternalUser.isPending ? (
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
          ) : (
            <UserPlus className='h-4 w-4 mr-2' />
          )}
          Create Internal User Registration
        </Button>
      </CardContent>
    </Card>
  );
}
